import { formatClock, formatDayClock } from '../../lib/format.ts'
import {
  responseActionLabel,
  responseBadges,
  responseStatusPresentation,
} from '../../lib/detections/semantics.ts'
import { blockIpSummary } from '../../lib/ip/semantics.ts'
import { processHref } from '../../lib/logs/investigation.ts'
import { Link } from '../../lib/router.tsx'
import type { AlertResponseAction } from '../../lib/sse/types.ts'
import { Badge } from '../primitives/Badge.tsx'
import { Notice } from '../primitives/States.tsx'

/**
 * Selo de UMA ação de resposta: "Isolar host · falhou". A ação e o desfecho
 * vão por extenso — a cor é o segundo canal (Badge.tsx) —, e um valor que o
 * console não conhece aparece como veio.
 */
export function ResponseBadge({ item }: { item: AlertResponseAction }) {
  const status = responseStatusPresentation(item.status)
  // "isolamento recomendado" já nomeia a ação: "Isolar host · …" seria eco.
  if (status.key === 'recommended' && item.action === 'isolate_host') {
    return (
      <Badge tone={status.tone} title={status.hint}>
        {status.label}
      </Badge>
    )
  }
  return (
    <Badge tone={status.tone} dashed={status.dashed} dot={status.tone === 'crit'} title={status.hint}>
      <span className="normal-case tracking-[0]">{responseActionLabel(item.action)}</span>
      {item.target?.ip != null && (
        <span className="font-mono normal-case tracking-[0]">{item.target.ip}</span>
      )}
      <span aria-hidden>·</span>
      {status.label}
    </Badge>
  )
}

/** Os selos de um alerta, deduplicados, falhas primeiro. Nada quando o agente não agiu. */
export function ResponseBadges({ response }: { response: readonly AlertResponseAction[] }) {
  if (response.length === 0) return null
  return (
    <span className="flex flex-wrap items-center gap-1">
      {responseBadges(response).map((item, i) => (
        <ResponseBadge key={`${item.action ?? '?'}|${item.status ?? '?'}|${i}`} item={item} />
      ))}
    </span>
  )
}

/**
 * A linha do tempo da resposta, na ficha do alerta: quando, o quê, em quem e o
 * texto do agente. O `detail` é texto livre do agente, exibido como texto.
 */
export function ResponseTimeline({ response }: { response: readonly AlertResponseAction[] }) {
  const failed = response.some((r) => r.status === 'failed')
  const allSimulated = response.length > 0 && response.every((r) => r.status === 'simulated')
  // Ordem cronológica quando dá; sem carimbo vai ao fim, na ordem em que veio.
  const ordered = response
    .map((item, index) => ({ item, index, at: item.timestamp === null ? NaN : Date.parse(item.timestamp) }))
    .sort((a, b) => {
      const aOk = Number.isFinite(a.at)
      const bOk = Number.isFinite(b.at)
      if (aOk && bOk) return a.at - b.at || a.index - b.index
      if (aOk !== bOk) return aOk ? -1 : 1
      return a.index - b.index
    })
    .map((x) => x.item)

  return (
    <div className="flex flex-col gap-2">
      {failed && (
        <Notice tone="crit">Uma ação de contenção FALHOU: a máquina continua comprometida.</Notice>
      )}
      {allSimulated && <Notice tone="neutral">Modo simulação: nenhuma ação foi executada.</Notice>}
      <ol className="flex flex-col">
        {ordered.map((item, i) => (
          <ResponseItem key={i} item={item} last={i === ordered.length - 1} />
        ))}
      </ol>
    </div>
  )
}

const DOT: Record<string, string> = {
  ok: 'bg-ok',
  crit: 'bg-crit',
  warn: 'bg-warn',
  accent: 'bg-accent',
  neutral: 'bg-line-strong',
}

function ResponseItem({ item, last }: { item: AlertResponseAction; last: boolean }) {
  const status = responseStatusPresentation(item.status)
  const target = item.target
  return (
    <li className="grid grid-cols-[1.25rem_1fr] gap-x-2">
      <span className="relative flex justify-center">
        {!last && <span aria-hidden className="absolute top-3 bottom-0 w-px bg-line" />}
        <span
          aria-hidden
          className={`relative mt-1.5 size-2 ${status.dashed ? 'border border-dashed border-fg-muted' : DOT[status.tone]}`}
        />
      </span>
      <span className="min-w-0 pb-3">
        <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          {item.timestamp !== null && (
            <span className="tnum font-mono text-micro text-fg-secondary" title={`${formatDayClock(item.timestamp)} UTC · relógio do agente`}>
              {formatClock(item.timestamp)}
            </span>
          )}
          <span className="text-sm text-fg">{responseActionLabel(item.action)}</span>
          <ResponseBadge item={item} />
        </span>
        {target !== null && (
          <span className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-micro text-fg-secondary">
            <span className="uppercase tracking-label text-fg-muted">alvo</span>
            {target.name !== null &&
              (target.entity_id !== null ? (
                <Link
                  href={processHref(target.entity_id)}
                  className="font-mono text-fg underline decoration-line-strong underline-offset-2 hover:text-accent"
                  title="Abrir a árvore e a linha do tempo do processo"
                >
                  {target.name}
                </Link>
              ) : (
                <span className="font-mono text-fg">{target.name}</span>
              ))}
            {target.pid !== null && <span className="tnum font-mono">PID {target.pid}</span>}
            {target.ip != null && <span className="font-mono text-fg">IP {target.ip}</span>}
          </span>
        )}
        {blockIpSummary(item) !== null && (
          <span className="mt-0.5 block text-sm text-fg">{blockIpSummary(item)}</span>
        )}
        {target?.executable != null && (
          <span className="mt-0.5 block border-l-2 border-line-strong bg-inset px-1.5 py-0.5 font-mono text-micro break-all text-fg-secondary">
            {target.executable}
          </span>
        )}
        {item.detail !== null && (
          <span className="mt-0.5 block text-micro leading-snug break-words text-fg-secondary">{item.detail}</span>
        )}
      </span>
    </li>
  )
}
